import "./fixTranslateCrash"; // must run before React renders
import { captureMarketingEntry } from "./lib/marketingTracking";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { exposeSandboxBuildMarker } from "./sandboxMarker";
import { installWorkspaceFetchContext } from "./lib/workspaceFetch";
import reportWebVitals from "./reportWebVitals";

function captureInviteTokenBeforeAnalytics(): void {
  if (window.location.pathname !== "/invite") return;
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const token = fragment.get("token");
  if (!token) return;
  window.sessionStorage.setItem("pendingInviteToken", token);
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

function scrubInviteUrl(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    const url = new URL(value, window.location.origin);
    if (url.pathname === "/invite" || url.pathname.startsWith("/invite/")) {
      url.pathname = "/invite";
      url.hash = "";
      return url.toString();
    }
  } catch {
    return value.replace(/\/invite\/[^/?#]+/g, "/invite");
  }
  return value;
}

// Fragments are not sent to WaveGen's server, but analytics/session replay can
// still observe location.href. Persist the capability and remove it before the
// PostHog provider initializes.
exposeSandboxBuildMarker();
captureInviteTokenBeforeAnalytics();
if (process.env.REACT_APP_META_TRACKING_ENABLED === "true") captureMarketingEntry();
installWorkspaceFetchContext();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
const app = <App />;
root.render(
  <React.StrictMode>
    {process.env.REACT_APP_POSTHOG_DISABLED === "true" ? (
      app
    ) : (
      <PostHogProvider
        apiKey={process.env.REACT_APP_POSTHOG_KEY || ""}
        options={{
          api_host: process.env.REACT_APP_POSTHOG_HOST,
          cross_subdomain_cookie: true,
          capture_pageview: false,
          capture_exceptions: true,
          before_send: (event) => {
            if (!event?.properties) return event;
            for (const key of [
              "$current_url",
              "$pathname",
              "$referrer",
              "$exception_url",
            ]) {
              event.properties[key] = scrubInviteUrl(event.properties[key]);
            }
            return event;
          },
          debug: process.env.NODE_ENV === "development",
        }}
      >
        {app}
      </PostHogProvider>
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
